import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plug, Loader2, Copy, RefreshCw, CheckCircle2, KeyRound, ShieldCheck,
  Boxes, Ticket as TicketIcon, ExternalLink, Eye, EyeOff, PlayCircle, XCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Copyable = ({ label, value, mask = false, testid }) => {
  const [show, setShow] = useState(!mask);
  const copy = () => { navigator.clipboard.writeText(value || ''); toast.success(`${label} copied`); };
  return (
    <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className="text-sm font-mono text-slate-800 truncate" data-testid={testid}>
          {show ? (value || '—') : '••••••••••••••••'}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {mask && (
          <button onClick={() => setShow(s => !s)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500" data-testid={`${testid}-toggle`}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
        <button onClick={copy} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500" data-testid={`${testid}-copy`}>
          <Copy className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wide">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
  </div>
);

export default function EticketBokunConnector() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/bokun/cm/admin/config`);
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Bókun connector configuration.');
      setCfg(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    if (!window.confirm('Rotate the Basic auth password? Bókun will need the new credentials to keep calling your plugin.')) return;
    setRotating(true);
    try {
      await axios.post(`${API}/api/bokun/cm/admin/rotate-credentials`, {});
      toast.success('Credentials rotated. Share the new password with Bókun.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rotate credentials.');
    } finally { setRotating(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/bokun/cm/admin/self-test`, {});
      setTestResult(data);
      data.ok ? toast.success('Plugin self-test passed') : toast.error('Plugin self-test found issues');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Self-test could not run.');
      setTestResult({ ok: false, steps: [], message: e.response?.data?.detail || 'Self-test could not run.' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#0C2159]" /></div>;
  if (!cfg) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700" data-testid="bokun-cm-error">
      We couldn't load an E-Ticket workspace for your account. Set up your attraction workspace first, then refresh.
    </div>
  );

  const p = cfg.plugin, prod = cfg.product, st = cfg.status;

  return (
    <div className="space-y-6" data-testid="bokun-cm-connector">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Plug className="w-6 h-6 text-[#0C2159]" /> SGMC Connect — Connectivity Providers
          </h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Connect the providers that power your SGMC distribution. Once a provider is active, its channels
            (shown on the <b>Distribution</b> tab) can be connected and synced. Bókun reaches Viator,
            GetYourGuide, Google Things to Do &amp; more; GlobalTix reaches Klook, Trip.com, KKday &amp; more.
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 shrink-0" data-testid="bokun-cm-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Provider layer overview */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="ota-providers">
        <div className="border border-[#0C2159]/20 bg-[#0C2159]/5 rounded-2xl p-4 flex items-center justify-between" data-testid="ota-provider-bokun">
          <div className="flex items-center gap-3">
            <img src="/ota-logos/bokun.png" alt="Bókun" className="h-8 w-auto rounded-md object-contain" data-testid="ota-provider-bokun-logo" />
            <div>
              <p className="font-semibold text-slate-900">Bókun</p>
              <p className="text-xs text-slate-500 mt-0.5">Viator · GetYourGuide · Google Things to Do · Airbnb · Headout</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="ota-provider-bokun-status">
            <CheckCircle2 className="w-4 h-4" /> Active
          </span>
        </div>
        <div className="border border-slate-200 bg-slate-50 rounded-2xl p-4 flex items-center justify-between opacity-90" data-testid="ota-provider-globaltix">
          <div className="flex items-center gap-3">
            <img src="/ota-logos/globaltix.png" alt="GlobalTix" className="h-8 w-auto rounded-md object-contain bg-white p-0.5" data-testid="ota-provider-globaltix-logo" />
            <div>
              <p className="font-semibold text-slate-700">GlobalTix</p>
              <p className="text-xs text-slate-500 mt-0.5">Klook · Trip.com · KKday · WAUG</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="ota-provider-globaltix-status">
            <RefreshCw className="w-4 h-4" /> Pending API
          </span>
        </div>
      </div>

      {/* Bókun provider config */}
      <div className="flex items-center gap-2 pt-1">
        <span className="w-2 h-2 rounded-full bg-[#0C2159]" />
        <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Bókun — Plugin Setup</p>
      </div>

      {/* Registration details */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="bokun-cm-registration">
        <p className="font-semibold text-slate-900 flex items-center gap-2 mb-1"><ShieldCheck className="w-4 h-4 text-[#0C2159]" /> Plugin Registration Details</p>
        <p className="text-xs text-slate-500 mb-4">Send these to the Bókun API team to register your Channel Manager plugin (transport: REST).</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Copyable label="Plugin name" value={p.name} testid="bokun-cm-name" />
          <Copyable label="Transport" value={p.transport} testid="bokun-cm-transport" />
          <Copyable label="Base URL (path)" value={p.base_url} testid="bokun-cm-baseurl" />
          <Copyable label="Definition URL" value={p.definition_url} testid="bokun-cm-defurl" />
          <Copyable label="Auth type" value={p.auth} testid="bokun-cm-authtype" />
          <Copyable label="Sandbox base" value={p.sandbox_base} testid="bokun-cm-sandbox" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <Copyable label="Basic username" value={p.basic_user} testid="bokun-cm-user" />
          <Copyable label="Basic password" value={p.basic_pass} mask testid="bokun-cm-pass" />
        </div>
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <div className="text-xs text-slate-500">
            Capabilities: {p.capabilities.map(c => <span key={c} className="inline-block bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 mr-1 font-mono text-[11px]">{c}</span>)}
          </div>
          <button onClick={rotate} disabled={rotating}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            data-testid="bokun-cm-rotate">
            {rotating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />} Rotate password
          </button>
        </div>
      </div>

      {/* Test my plugin */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="bokun-cm-selftest">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="font-semibold text-slate-900 flex items-center gap-2"><PlayCircle className="w-4 h-4 text-[#0C2159]" /> Test my plugin</p>
            <p className="text-xs text-slate-500 mt-0.5">Runs definition → availability → reserve → confirm against your own endpoint (auto-cleans up). Do this before handing the plugin to Bókun.</p>
          </div>
          <button onClick={runTest} disabled={testing}
            className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-[#0C2159] text-white hover:bg-[#0a1a47] disabled:opacity-60 shrink-0"
            data-testid="bokun-cm-selftest-run">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Run test
          </button>
        </div>
        {testResult && (
          <div className="mt-4 space-y-2" data-testid="bokun-cm-selftest-result">
            {(testResult.steps || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`bokun-cm-selftest-step-${i}`}>
                {s.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                <span className="font-medium text-slate-700">{s.step}</span>
                {s.detail && <span className="text-xs text-slate-400">— {s.detail}</span>}
              </div>
            ))}
            <div className={`mt-2 text-sm font-semibold flex items-center gap-2 ${testResult.ok ? 'text-emerald-600' : 'text-red-500'}`}>
              {testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {testResult.message}
            </div>
          </div>
        )}
      </div>

      {/* Configuration parameter helper */}
      <div className="bg-[#0C2159]/5 border border-[#0C2159]/15 rounded-2xl p-5" data-testid="bokun-cm-configparam">
        <p className="font-semibold text-slate-900 flex items-center gap-2 mb-1"><Boxes className="w-4 h-4 text-[#0C2159]" /> Configure in the Bókun Extranet</p>
        <p className="text-xs text-slate-500 mb-3">
          After Bókun assigns the plugin, go to <b>Settings → Connections → Channel Manager → Configure plugin</b> and set:
        </p>
        <Copyable label="company_slug (config parameter)" value={prod.external_id} testid="bokun-cm-companyslug" />
        <p className="text-xs text-slate-400 mt-2">This routes every Bókun request to <b>{prod.name}</b>.</p>
      </div>

      {/* Mapped products */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="bokun-cm-products">
        <p className="font-semibold text-slate-900 flex items-center gap-2 mb-3"><TicketIcon className="w-4 h-4 text-[#0C2159]" /> Exposed Product &amp; Pricing Categories</p>
        <div className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 mb-2">
          <span className="font-semibold text-slate-700">{prod.name}</span>
          <span className="text-xs text-slate-400 font-mono">id: {prod.external_id} · {prod.currency}</span>
        </div>
        <div className="space-y-1.5">
          {prod.pricing_categories.map(pc => (
            <div key={pc.id} className="flex items-center justify-between text-sm" data-testid={`bokun-cm-pc-${pc.id}`}>
              <span className="text-slate-700">{pc.label}</span>
              <span className="font-semibold text-slate-900">{prod.currency} {pc.price.toFixed(2)}</span>
            </div>
          ))}
          {prod.pricing_categories.length === 0 && <p className="text-sm text-slate-400">No active ticket types. Add products first.</p>}
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Daily capacity: {prod.daily_capacity === 0 ? 'Unlimited' : `${prod.daily_capacity} / day`} (set under Distribution).
        </p>
      </div>

      {/* Sync status */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="bokun-cm-status">
        <Stat label="Reservations" value={st.reservations} icon={RefreshCw} testid="bokun-cm-stat-res" />
        <Stat label="Bookings" value={st.bookings} icon={CheckCircle2} testid="bokun-cm-stat-bk" />
        <Stat label="Confirmed" value={st.confirmed_bookings} icon={CheckCircle2} testid="bokun-cm-stat-conf" />
        <Stat label="Tickets issued" value={st.tickets_issued} icon={TicketIcon} testid="bokun-cm-stat-tickets" />
      </div>

      {/* GlobalTix — pending */}
      <div className="flex items-center gap-2 pt-1">
        <span className="w-2 h-2 rounded-full bg-[#0aa15a]" />
        <p className="text-sm font-semibold text-slate-700 uppercase tracking-wide">GlobalTix — Pending</p>
      </div>
      <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-5 text-sm text-slate-500" data-testid="globaltix-pending">
        GlobalTix distribution (Klook, Trip.com, KKday, WAUG) is built on the same provider-neutral engine and
        will activate here once GlobalTix API credentials &amp; documentation are provided. No changes to your
        products or the Distribution page are needed — the channels already show a “Powered by GlobalTix · pending” badge.
      </div>

      <a href="https://bokun.dev/channel-manager-api/rGmzgGe66zjtEQ8FUvS8dd" target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-[#0C2159] font-semibold hover:underline" data-testid="bokun-cm-docs">
        <ExternalLink className="w-4 h-4" /> Bókun Channel Manager API documentation
      </a>
    </div>
  );
}
